<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import { buildTrackingUrl } from './attributionUtils';

// "Constructor de enlaces" card of the CrmB mockup: pick an existing code
// and it composes the URL with every UTM already in place, ready to paste
// into the ad.
const props = defineProps({
  campaigns: { type: Array, default: () => [] },
  defaultUrl: { type: String, default: '' },
});

const { t } = useI18n();

const selectedCode = ref('');
const landingUrl = ref(props.defaultUrl);

// Preselect the most recent code once the list arrives.
watch(
  () => props.campaigns,
  campaigns => {
    if (!selectedCode.value && campaigns.length) {
      selectedCode.value = campaigns[0].code;
    }
  },
  { immediate: true }
);

const selectedCampaign = computed(() =>
  props.campaigns.find(campaign => campaign.code === selectedCode.value)
);

const utms = computed(() => selectedCampaign.value?.utms || {});

const readOnlyFields = computed(() => [
  { key: 'source', label: t('ATTRIBUTION.BUILDER.SOURCE') },
  { key: 'medium', label: t('ATTRIBUTION.BUILDER.MEDIUM') },
  { key: 'campaign', label: t('ATTRIBUTION.BUILDER.CAMPAIGN') },
  { key: 'content', label: t('ATTRIBUTION.BUILDER.CONTENT') },
]);

const builtUrl = computed(() =>
  buildTrackingUrl(landingUrl.value, utms.value, selectedCode.value)
);

const copyUrl = async () => {
  if (!builtUrl.value) return;
  try {
    await copyTextToClipboard(builtUrl.value);
    useAlert(t('ATTRIBUTION.BUILDER.COPIED'));
  } catch {
    useAlert(t('ATTRIBUTION.BUILDER.COPY_ERROR'));
  }
};

const FIELD_CLASS =
  'w-full h-[33px] px-2 rounded-lg border border-n-weak bg-n-alpha-1 text-[12.5px] text-n-slate-12 outline-none focus:bg-n-solid-2 focus:border-n-brand/60 transition-colors reset-base';
const LABEL_CLASS = 'text-[11px] font-semibold text-n-slate-10';
</script>

<template>
  <section
    class="flex flex-col gap-3 px-[18px] py-4 rounded-xl border border-n-weak bg-n-solid-2"
  >
    <div class="flex flex-wrap items-center gap-2.5">
      <h2
        class="m-0 text-[13.5px] font-semibold tracking-tight text-n-slate-12"
      >
        {{ t('ATTRIBUTION.BUILDER.TITLE') }}
      </h2>
      <span class="text-[11.5px] text-n-slate-10">
        {{ t('ATTRIBUTION.BUILDER.SUBTITLE') }}
      </span>
    </div>

    <div class="grid grid-cols-1 gap-2.5 md:grid-cols-[1fr_1.2fr]">
      <label class="flex flex-col gap-1.5">
        <span :class="LABEL_CLASS">{{ t('ATTRIBUTION.BUILDER.CODE') }}</span>
        <select v-model="selectedCode" :class="FIELD_CLASS">
          <option v-if="!campaigns.length" value="">
            {{ t('ATTRIBUTION.BUILDER.NO_CODES') }}
          </option>
          <option
            v-for="campaign in campaigns"
            :key="campaign.id"
            :value="campaign.code"
          >
            {{ campaign.code }} ·
            {{ campaign.description || campaign.utms?.campaign || '—' }}
          </option>
        </select>
      </label>
      <label class="flex flex-col gap-1.5">
        <span :class="LABEL_CLASS">{{ t('ATTRIBUTION.BUILDER.LANDING') }}</span>
        <input
          v-model="landingUrl"
          :class="FIELD_CLASS"
          :placeholder="t('ATTRIBUTION.BUILDER.LANDING_PLACEHOLDER')"
        />
      </label>
    </div>

    <div class="grid grid-cols-2 gap-2.5 md:grid-cols-4">
      <div
        v-for="field in readOnlyFields"
        :key="field.key"
        class="flex flex-col gap-1.5 min-w-0"
      >
        <span :class="LABEL_CLASS">{{ field.label }}</span>
        <span
          class="flex items-center h-[33px] px-2 rounded-lg bg-n-alpha-1 text-[12.5px] truncate"
          :class="utms[field.key] ? 'text-n-slate-12' : 'text-n-slate-9'"
          :title="utms[field.key] || ''"
        >
          {{ utms[field.key] || '—' }}
        </span>
      </div>
    </div>

    <div
      class="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-n-blue-border bg-n-blue-2"
    >
      <span
        class="flex-1 min-w-0 text-xs font-mono truncate"
        :class="builtUrl ? 'text-n-blue-text' : 'text-n-slate-9'"
        :title="builtUrl"
      >
        {{ builtUrl || t('ATTRIBUTION.BUILDER.EMPTY_URL') }}
      </span>
      <button
        type="button"
        class="inline-flex items-center flex-shrink-0 gap-1.5 h-7 px-3 rounded-[7px] bg-n-brand text-white text-[11.5px] font-semibold transition-all hover:brightness-90 disabled:opacity-50"
        :disabled="!builtUrl"
        @click="copyUrl"
      >
        <Icon icon="i-lucide-copy" size="size-3" />
        {{ t('ATTRIBUTION.BUILDER.COPY') }}
      </button>
    </div>
  </section>
</template>
